import {
  Box,
  Button,
  Modal as ChakraModal,
  Flex,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import ReactInputMask from 'react-input-mask'
import ReactSelect from 'react-select'

import { JobApplicationService } from '../../../services/JobApplication/JobApplicationService'
import {
  JobApplicationComplete,
  JobApplicationPositions,
  JobApplicationStatuses
} from '../../../services/JobApplication/types'
import { statusInfo } from './JobApplicationPage'

interface IProps {
  isOpen: boolean
  onClose: () => void
  positions: JobApplicationPositions
  jobApplication?: JobApplicationComplete
}

export interface IJobApplicationFields {
  name: string
  email: string
  phone: string
  position: string
  status: JobApplicationStatuses
  area: string[]
  why_you_want_to_work_here: string
  reasons_to_hire_you: string
  resume: File | null
}

const baseForm: IJobApplicationFields = {
  name: '',
  email: '',
  phone: '',
  position: '',
  status: 'new',
  area: [],
  why_you_want_to_work_here: '',
  reasons_to_hire_you: '',
  resume: null
}

const baseFormErrors = {
  name: '',
  email: '',
  phone: '',
  status: '',
  position: '',
  why_you_want_to_work_here: '',
  reasons_to_hire_you: '',
  resume: ''
}

export function JobApplicationFormModal({ isOpen, onClose, positions, jobApplication }: IProps) {
  const service = new JobApplicationService()
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const [form, setForm] = useState<IJobApplicationFields>(baseForm)
  const [areas, setAreas] = useState<string[]>([])
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: '',
    status: '',
    position: '',
    why_you_want_to_work_here: '',
    reasons_to_hire_you: '',
    resume: ''
  })

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)
      setForm(
        jobApplication
          ? {
              name: jobApplication.name,
              email: jobApplication.email,
              phone: jobApplication.phone,
              position: jobApplication.job_position,
              status: jobApplication.status,
              area: jobApplication.area || '',
              why_you_want_to_work_here: jobApplication.why_you_want_to_work_here || '',
              reasons_to_hire_you: jobApplication.reasons_to_hire_you || '',
              resume: null
            }
          : baseForm
      )
    }
  }, [isOpen])

  useEffect(() => {
    setAreas(positions.find((position) => position.name === form.position)?.areas || [])
  }, [form.position, positions])

  const handleSave = async () => {
    setSaving(true)

    const saveFunction = jobApplication ? service.update(jobApplication.id, form) : service.create(form)

    saveFunction
      .then(() => {
        toast({
          title: 'Registro salvo com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        onClose()
      })
      .catch((response) => {
        if (response.response.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast({
          title: jobApplication ? 'Erro ao editar candidato' : 'Erro ao adicionar candidato',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{jobApplication ? 'Editar' : 'Registrar'} candidato</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={3}>
            <Flex direction="row" gap={3}>
              <Box w="75%">
                <FormLabel htmlFor="name">Nome</FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  id="name"
                  disabled={saving}
                />
                {formErrors.name && <Text color="red.500">{formErrors.name}</Text>}
              </Box>
              <Box w="25%">
                <FormLabel htmlFor="status">Status</FormLabel>
                <Select
                  id="status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as JobApplicationStatuses })}
                  disabled={saving}
                >
                  {Object.entries(statusInfo).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value.label}
                    </option>
                  ))}
                </Select>
                {formErrors.status && <Text color="red.500">{formErrors.status}</Text>}
              </Box>
            </Flex>

            <Flex direction="row" gap={3}>
              <Box w="40%">
                <FormLabel htmlFor="phone">Telefone</FormLabel>
                <Input
                  value={form.phone}
                  as={ReactInputMask}
                  mask="(99) 99999-9999"
                  maskChar={null}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  id="phone"
                  disabled={saving}
                />
                {formErrors.phone && <Text color="red.500">{formErrors.phone}</Text>}
              </Box>

              <Box w="60%">
                <FormLabel htmlFor="email">E-mail</FormLabel>
                <Input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  id="email"
                  disabled={saving}
                />
                {formErrors.email && <Text color="red.500">{formErrors.email}</Text>}
              </Box>
            </Flex>

            <Box>
              <FormLabel htmlFor="position">Vaga</FormLabel>
              <Select
                id="position"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                disabled={saving}
              >
                <option value="">Selecione uma vaga</option>
                {positions.map(({ name }) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </Box>

            {areas.length > 0 && (
              <Box>
                <FormLabel htmlFor="area">Área</FormLabel>
                <ReactSelect
                  id="area"
                  value={form.area ? form.area.map((area) => ({ label: area, value: area })) : []}
                  disabled={saving}
                  isMulti
                  closeMenuOnSelect={false}
                  options={areas.map((area) => ({
                    label: area,
                    value: area
                  }))}
                  onChange={(selectedOptions: { value: string }[]) => {
                    const selectedValues = selectedOptions.map((option: { value: string }) => option.value)
                    setForm({ ...form, area: selectedValues })
                  }}
                  placeholder="Selecione uma ou mais áreas"
                />
              </Box>
            )}

            <Box>
              <FormLabel htmlFor="resume">Currículo</FormLabel>
              <Input
                type="file"
                id="resume"
                accept=".pdf, .doc, .docx, .png, .jpg, .jpeg"
                p={1}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    setForm({ ...form, resume: file })
                  }
                }}
                disabled={saving}
                required={!jobApplication}
              />
              {formErrors.resume && <Text color="red.500">{formErrors.resume}</Text>}
            </Box>

            <Box>
              <FormLabel htmlFor="why_you_want_to_work_here">Por que você quer trabalhar aqui?</FormLabel>
              <Textarea
                value={form.why_you_want_to_work_here}
                disabled={saving}
                height="110px"
                onChange={(e) => setForm({ ...form, why_you_want_to_work_here: e.target.value })}
              />
              {formErrors.why_you_want_to_work_here && (
                <Text color="red.500">{formErrors.why_you_want_to_work_here}</Text>
              )}
            </Box>

            <Box>
              <FormLabel htmlFor="reasons_to_hire_you">Razões para te contratar</FormLabel>
              <Textarea
                value={form.reasons_to_hire_you}
                disabled={saving}
                height="110px"
                onChange={(e) => setForm({ ...form, reasons_to_hire_you: e.target.value })}
              />
              {formErrors.reasons_to_hire_you && <Text color="red.500">{formErrors.reasons_to_hire_you}</Text>}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}
